/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * @typedef {import('swiper').Swiper} Swiper
 *
 * Escape hatch for banner video slides.
 *
 * Carousel autoplay is stopped while a video slide is active — `ended` advances the banner.
 * An embed that never reaches `ended` parks the hero forever unless something moves it on.
 *
 * No-progress budget: playback position is sampled once per window and the window renews when
 * it advanced, so a long video plays in full while a dead one is abandoned quickly.
 *
 * Deliberately ignores `swiper.banner.playing`. A player that stalls after `play` leaves a
 * stale entry; treating that as health turns a transient embed failure into a dead carousel.
 */

/**
 * How long the active video may make no progress before the carousel moves on. Also covers a
 * cold start (oembed / iframe / handshake) — the first sample starts from an unset position
 * so a genuine stall at 0 still gets a second window.
 */
const NO_PROGRESS_BUDGET = 6000;

/**
 * @param {Swiper} swiper
 *
 * @returns {HTMLElement|null}
 */
function activeVideoElement(swiper) {
  return swiper.slides?.[swiper.realIndex]?.querySelector('[amb-video]') || null;
}

/**
 * @param {Swiper} swiper
 *
 * @returns {Promise<number|null>}
 */
function readPlaybackPosition(swiper) {
  const player = activeVideoElement(swiper)?.player;

  // Never route through a player that is not ready — Player methods await waitUntilReady(),
  // which never settles for a failed embed.
  if (!player?.isReady) {
    return Promise.resolve(null);
  }

  return player
    .call('getCurrentTime')
    .then((time) => (typeof time === 'number' ? time : null))
    .catch(() => null)
  ;
}

export const bannerVideoWatchdog = {
  /**
   * @param {Swiper} swiper
   */
  arm(swiper) {
    this.clear(swiper);

    if (!activeVideoElement(swiper) || swiper.banner?.userPaused) {
      return;
    }

    // `-1` so the first sample always counts as progress (cold-start grace).
    this.schedule(swiper, swiper.realIndex, -1);
  },

  /**
   * @param {Swiper} swiper
   */
  clear(swiper) {
    clearTimeout(swiper.banner?.escapeTimer);

    if (swiper.banner) {
      swiper.banner.escapeTimer = null;
    }
  },

  /**
   * @protected
   *
   * @param {Swiper} swiper
   * @param {number} realIndex
   * @param {number} lastTime
   */
  schedule(swiper, realIndex, lastTime) {
    if (!swiper.banner) {
      return;
    }

    swiper.banner.escapeTimer = setTimeout(() => {
      if (!this.stillWatching(swiper, realIndex)) {
        return;
      }

      readPlaybackPosition(swiper).then((time) => {
        if (!this.stillWatching(swiper, realIndex)) {
          return;
        }

        if (time !== null && time > lastTime) {
          this.schedule(swiper, realIndex, time);

          return;
        }

        // Reset bookkeeping the video would normally clear on ended.
        if (swiper.banner) {
          swiper.banner.wasOnVideo = false;
          swiper.banner.playing.clear();
        }

        swiper.slideNext();
      });
    }, NO_PROGRESS_BUDGET);
  },

  /**
   * @protected
   *
   * @param {Swiper} swiper
   * @param {number} realIndex
   *
   * @returns {boolean}
   */
  stillWatching(swiper, realIndex) {
    return !swiper.destroyed
      && swiper.realIndex === realIndex
      && !!activeVideoElement(swiper)
      && !swiper.banner?.userPaused
    ;
  }
};
